/**
 * Which modality/specialization picks survive a change of service.
 *
 * The lists are keyed by the service code, so moving a therapist to another profession can
 * strand values the new one does not offer. Returns the values to keep, or `null` for "leave
 * the field alone" -- the caller only writes when this returns an array, so opening a form
 * never marks it dirty.
 *
 * Pure and separate from the component because the two skip rules are the load-bearing part
 * and both fail silently:
 *
 * - `previousCode === undefined` is the untouched sentinel, distinct from `""`/`null` (a form
 *   with no service picked yet). Thousands of therapists predate these lists and hold free
 *   text; pruning on mount would erase it while the admin was editing their phone number.
 * - An unchanged code short-circuits because the caller's options array is rebuilt by a memo,
 *   so a fresh reference on an unrelated prop change must not count as a service switch.
 *
 * An empty `options` means the new service keeps this field as free text. The values still go:
 * carrying one profession's vocabulary into another's record is what these lists exist to
 * prevent, and the model would accept it silently because it has no list to check against.
 */
export function prunedValues(
	previousCode: string | null | undefined,
	currentCode: string | null | undefined,
	options: string[],
	current: string[],
): string[] | null {
	if (previousCode === undefined) return null;
	if (previousCode === currentCode) return null;

	const kept = current.filter((value) => options.includes(value));

	return kept.length === current.length ? null : kept;
}
